import type { ApiSpinTogetherDisplayConfigV2 } from "@bridge-applications/pb-types";
import { Trans, useTranslation } from "react-i18next";
import timerIcon from "../../../../../../../../../assets/static/icons/spin-together/timer-icon.webp";
import energyCanIcon from "../../../../../../../../../assets/static/images/symbols/v1/symbol-energy-drink.webp";
import { formatMinorUnits } from "../../../../../../../../../logic/helpers/currencyAmount";
import { resolveSupportedLocale } from "../../../../../../../../../logic/i18n/locales";
import { ButtonVar1 } from "../../../../../../../../common/ButtonVar1/ButtonVar1";
import { SimpleDialog } from "../../../../../../../../common/SimpleDialog/SimpleDialog";
import "./SpinTogetherRulesDialog.scss";

const FRIEND_BOOST_TITLE_ASSET_PREFIX =
    "../../../../../../../../../assets/static/images/spin-together/friend-boost-title/friend-boost-title__";
const friendBoostTitleAssets = import.meta.glob<string>(
    "../../../../../../../../../assets/static/images/spin-together/friend-boost-title/friend-boost-title__*.webp",
    { eager: true, import: "default" },
);
const defaultFriendBoostTitleAsset =
    friendBoostTitleAssets[`${FRIEND_BOOST_TITLE_ASSET_PREFIX}en.webp`] ??
    (() => {
        throw new Error("Missing default localized friend-boost title asset");
    })();

function getFriendBoostTitleAsset(locale: string): string {
    const assetLocale = resolveSupportedLocale(locale).toLowerCase();
    return (
        friendBoostTitleAssets[
            `${FRIEND_BOOST_TITLE_ASSET_PREFIX}${assetLocale}.webp`
        ] ?? defaultFriendBoostTitleAsset
    );
}

interface SpinTogetherRulesDialogProps {
    config: ApiSpinTogetherDisplayConfigV2;
    isOpen: boolean;
    locale: string;
    onClose: () => void;
    percentage: string;
    timeLeft: string;
}

export function SpinTogetherRulesDialog({
    config,
    isOpen,
    locale,
    onClose,
    percentage,
    timeLeft,
}: SpinTogetherRulesDialogProps) {
    const { t } = useTranslation();
    const energy = config.sessionStartEnergy;
    const energyAmount = energy.enabled
        ? formatMinorUnits(energy.amountPerParticipant, 0, locale)
        : null;

    return (
        <SimpleDialog
            className="spin-together-rules-dialog"
            isOpen={isOpen}
            onClose={onClose}
            aria-labelledby="spin-together-rules-title"
        >
            <article className="spin-together-rules-dialog__card">
                <h2
                    className="spin-together-rules-dialog__title"
                    id="spin-together-rules-title"
                >
                    <span className="spin-together-rules-dialog__title-label">
                        {t("spinTogether.rules.title")}
                    </span>
                    <img
                        className="spin-together-rules-dialog__title-image"
                        src={getFriendBoostTitleAsset(locale)}
                        alt=""
                        aria-hidden="true"
                        draggable={false}
                    />
                </h2>

                <div className="spin-together-rules-dialog__panel">
                    <ol className="spin-together-rules-dialog__rules">
                        {energyAmount && (
                            <li>
                                <Trans
                                    i18nKey="spinTogether.rules.energyRule"
                                    values={{ amount: energyAmount }}
                                    components={{
                                        highlight: <strong />,
                                        energyIcon: (
                                            <img
                                                className="spin-together-rules-dialog__energy-icon"
                                                src={energyCanIcon}
                                                alt={t(
                                                    "spinTogether.rules.energyTitle",
                                                )}
                                                draggable={false}
                                            />
                                        ),
                                    }}
                                />
                            </li>
                        )}
                        <li>
                            <Trans
                                i18nKey="spinTogether.rules.boostRule"
                                values={{ percentage, timeLeft }}
                                components={{
                                    highlight: <strong />,
                                    time: (
                                        <span
                                            className="spin-together-rules-dialog__time"
                                            dir="ltr"
                                        />
                                    ),
                                    timerIcon: (
                                        <img
                                            className="spin-together-rules-dialog__timer-icon"
                                            src={timerIcon}
                                            alt=""
                                            aria-hidden="true"
                                            draggable={false}
                                        />
                                    ),
                                }}
                            />
                        </li>
                    </ol>
                </div>

                <ButtonVar1
                    className="spin-together-rules-dialog__confirm"
                    text={t("spinTogether.rules.ok")}
                    onClick={onClose}
                />
            </article>
        </SimpleDialog>
    );
}
